import { t } from './i18n.js';
import { useCallback, useEffect, useState } from 'react';
import { CalendarClock, Clock, Pencil, Trash2 } from 'lucide-react';
import type { Submission } from '../shared/types.js';
import { api, send } from './api.js';
import { IconButton, Loading, Modal, Notice, displayDate } from './components.js';
import {
  browserZone,
  exactLocalToIso,
  formatInZone,
  isoToLocal,
  localToIso,
  zones,
} from './time.js';

export function Scheduled({
  composeOpen,
  notify,
  onEdit,
  onChange,
}: {
  composeOpen: boolean;
  notify: (s: string) => void;
  onEdit: (draftId: string) => Promise<void>;
  onChange: () => void;
}) {
  const [items, setItems] = useState<Submission[]>(),
    [error, setError] = useState(''),
    [confirm, setConfirm] = useState<Submission>(),
    // Moving a departure keeps the message as it is; only the instant changes.
    [moving, setMoving] = useState<{ item: Submission; when: string; zone: string }>(),
    [busy, setBusy] = useState(false);
  const load = useCallback(
    () =>
      api<Submission[]>('/submissions/scheduled')
        .then((r) => {
          setItems(r);
          setError('');
        })
        .catch((e) => setError(e.message)),
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const cancel = async (s: Submission, thenEdit = false) => {
    // Checked before cancelling: the open message must not be replaced by this draft.
    if (thenEdit && composeOpen) {
      notify(t('Un message est déjà ouvert. Vous pouvez le fermer en conservant son brouillon.'));
      return;
    }
    setBusy(true);
    try {
      await send(`/submissions/${s.id}/cancel`, {});
      setConfirm(undefined);
      notify(
        thenEdit
          ? t('Programmation annulée : le brouillon est de nouveau modifiable.')
          : t('Envoi annulé.'),
      );
      onChange();
      await load();
      if (thenEdit && s.draftId) await onEdit(s.draftId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const departure = moving ? exactLocalToIso(moving.when, moving.zone) : '';
  const skipped = Boolean(
    moving && moving.when && localToIso(moving.when, moving.zone) && !departure,
  );
  const reschedule = async () => {
    if (!moving) return;
    if (!departure) {
      setError(
        skipped
          ? t('Cette heure n’existe pas dans ce fuseau, à cause du changement d’heure.')
          : t('Choisissez une date et une heure.'),
      );
      return;
    }
    setBusy(true);
    try {
      await send(`/submissions/${moving.item.id}/reschedule`, { scheduledAt: departure });
      setMoving(undefined);
      setError('');
      notify(t('Nouvelle heure d’envoi enregistrée.'));
      onChange();
      await load();
    } catch (e: any) {
      setError(e.message);
      // The message may have left meanwhile: the list shows what is really still waiting.
      await load();
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="workspace-page scheduled-page">
      <header className="page-heading">
        <div>
          <span className="eyebrow">{t('À VENIR')}</span>
          <h1>{t('Envois programmés.')}</h1>
          <p>
            {t(
              t(
                'Ces messages partiront à l’heure choisie. Les droits, le quota et les limites d’envoi sont vérifiés au moment du départ.',
              ),
            )}
          </p>
        </div>
      </header>
      <section className="admin-card scheduled-card">
        {error && <Notice type="error">{error}</Notice>}
        {!items ? (
          <Loading label={t('Chargement des envois programmés…')} />
        ) : items.length === 0 ? (
          <div className="empty-state">
            <CalendarClock size={30} />
            <h3>{t('Aucun envoi programmé.')}</h3>
            <p>
              {t(
                t(
                  'Dans un nouveau message, choisissez « Programmer l’envoi » pour différer son départ.',
                ),
              )}
            </p>
          </div>
        ) : (
          <ul className="scheduled-list">
            {items.map((s) => (
              <li key={s.id}>
                <span className="scheduled-body">
                  <strong>{s.subject || t('(sans objet)')}</strong>
                  <small>À : {s.recipients.map((r) => r.address).join(', ')}</small>
                  <small className="scheduled-when">
                    <CalendarClock size={14} /> Envoi le {displayDate(s.scheduledAt!, true)}
                  </small>
                  {s.error && <small className="scheduled-error">{s.error}</small>}
                </span>
                <span className="rule-tools">
                  <IconButton
                    label={t('Changer l’heure d’envoi de {subject}', {
                      subject: s.subject || t('ce message'),
                    })}
                    onClick={() =>
                      setMoving({
                        item: s,
                        zone: browserZone(),
                        when: isoToLocal(s.scheduledAt!, browserZone()),
                      })
                    }
                    disabled={busy}
                  >
                    <Clock size={17} />
                  </IconButton>
                  {s.draftId && (
                    <IconButton
                      label={t('Modifier {subject}', { subject: s.subject || t('le message') })}
                      onClick={() => void cancel(s, true)}
                      disabled={busy}
                    >
                      <Pencil size={17} />
                    </IconButton>
                  )}
                  <IconButton
                    label={t('Annuler l’envoi de {subject}', {
                      subject: s.subject || t('ce message'),
                    })}
                    onClick={() => setConfirm(s)}
                    disabled={busy}
                  >
                    <Trash2 size={17} />
                  </IconButton>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
      {confirm && (
        <Modal title={t('Annuler cet envoi')} onClose={() => setConfirm(undefined)}>
          <p className="modal-body">
            {t(
              'Le message « {subject} » ne sera pas envoyé. Son brouillon reste dans vos brouillons.',
              {
                subject: confirm.subject || t('(sans objet)'),
              },
            )}
          </p>
          <div className="modal-actions">
            <button className="text-button" onClick={() => setConfirm(undefined)}>
              {t('Conserver')}
            </button>
            <button className="danger" onClick={() => void cancel(confirm)} disabled={busy}>
              {t('Annuler l’envoi')}
            </button>
          </div>
        </Modal>
      )}
      {moving && (
        <Modal title={t('Changer l’heure d’envoi')} onClose={() => setMoving(undefined)}>
          <form
            className="contact-form"
            onSubmit={(e) => {
              e.preventDefault();
              void reschedule();
            }}
          >
            <label htmlFor="reschedule-when">{t('Envoyer le')}</label>
            <input
              id="reschedule-when"
              type="datetime-local"
              value={moving.when}
              onChange={(e) => setMoving({ ...moving, when: e.target.value })}
            />
            <label htmlFor="reschedule-zone">{t('Fuseau horaire')}</label>
            <select
              id="reschedule-zone"
              value={moving.zone}
              onChange={(e) =>
                // The wall clock follows the zone, so the chosen instant does not move by itself.
                setMoving({
                  ...moving,
                  zone: e.target.value,
                  when: departure ? isoToLocal(departure, e.target.value) : moving.when,
                })
              }
            >
              {[...new Set([...zones, browserZone()])].map((z) => (
                <option key={z}>{z}</option>
              ))}
            </select>
            <small role={skipped ? 'alert' : undefined}>
              {departure
                ? t('Départ le {date} ({zone}).', {
                    date: formatInZone(departure, moving.zone),
                    zone: moving.zone,
                  })
                : skipped
                  ? t('Cette heure n’existe pas dans ce fuseau, à cause du changement d’heure.')
                  : t('Choisissez une date et une heure.')}
            </small>
            <div className="modal-actions">
              <button type="button" className="text-button" onClick={() => setMoving(undefined)}>
                {t('Annuler')}
              </button>
              <button type="submit" className="primary" disabled={busy || !departure}>
                {t('Enregistrer l’heure')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </main>
  );
}
